import { useAddToTrash } from "@/api/trash";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export type DashboardGridCardActionsProps<T extends { id: string }> = {
  documentType: string;
  item: T;
  onEdit: (item: T) => void;
  publicationStatusField?: keyof T;
  /**
   * Extra actions inserted between "edit" and "delete" (e.g. "duplicate").
   * Receives the current item so callers can build item-specific handlers.
   */
  buildAdditionalActions?: (item: T) => TableRowAction[];
};

export function DashboardGridCardActions<T extends { id: string }>({
  documentType,
  item,
  onEdit,
  publicationStatusField,
  buildAdditionalActions,
}: DashboardGridCardActionsProps<T>) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader";
  const trashAdder = useAddToTrash();

  const publicationStatus = publicationStatusField
    ? (item[publicationStatusField] as string | undefined)
    : undefined;

  const handleAddToTrash = () => {
    if (publicationStatus === "bin") {
      toast({
        title: t("documentAlreadyInTrashToastTitle"),
        variant: "success",
        description: t("documentAlreadyInTrashToastDescription"),
      });
      return;
    }

    trashAdder.mutate(
      { documentId: item.id, documentType },
      {
        onSuccess: () => {
          toast({
            title: t("documentAddedToTrashToastTitle"),
            variant: "success",
            description: t("documentAddedToTrashToastDescription"),
          });
        },
      },
    );
  };

  const actions: TableRowAction[] = [
    {
      id: "edit",
      label: t(ACTION_I18N_KEYS.edit),
      icon: Pencil,
      onSelect: () => onEdit(item),
    },
    ...(buildAdditionalActions?.(item) ?? []),
    {
      id: "delete",
      label: t(ACTION_I18N_KEYS.delete),
      icon: Trash2,
      destructive: true,
      hidden: isReader,
      disabled: trashAdder.isPending || publicationStatus === "bin",
      onSelect: handleAddToTrash,
    },
  ];

  return <TableRowActionsMenu actions={actions} />;
}
